/* Placing the camera from all six marks at once.

   solveCalibration recovers the pose from the FIRST panel's four marks alone,
   and core/corner.js then back-projects the two outer marks through it without
   ever letting them push back. That split is deliberate and it is what makes
   the residual honest: the outer marks take no part in the fit, so their
   disagreement is a genuine independent test of the two assumptions the corner
   rests on — that it is square, and that the panels share a height.

   It is also, for rendering, the wrong camera. Every bit of error the marks
   carry — and hand-placed marks on a photograph always carry some — lands
   entirely on the second panel, because the first panel's homography is fitted
   to its own quad and absorbs anything the pose gets wrong. So panel one looks
   right no matter what, Flat output looks right (each panel is rendered through
   its own frustum with no photograph involved), and the error surfaces in
   exactly one place: On-site, at the seam, growing the further content stands
   off the screen.

   This refines that pose over all six marks. The corner stays at 90° and the
   panels stay as measured — nothing about the GEOMETRY is being fitted, only
   where the camera stood. Error that used to be dumped on the second panel is
   shared out instead.

   The diagnostic is deliberately NOT computed from this. cornerCheck still
   reports the panel-one-only fit, so "your marks do not describe a square
   corner" stays a thing the app can say. Refining first would bury exactly
   that signal, which is the objection the feature was originally shelved over
   — the answer is to do both, and to use each for the thing it is good at. */

/** 3×3 multiply, rows × columns. */
function mul(A, B) {
  return A.map((r, i) => B[0].map((_, j) => r.reduce((s, _v, k) => s + A[i][k] * B[k][j], 0)));
}

/** Rodrigues — a rotation of |w| radians about w. */
function rodrigues(w) {
  const th = Math.hypot(w[0], w[1], w[2]);
  if (th < 1e-12) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  const k = [w[0] / th, w[1] / th, w[2] / th];
  const c = Math.cos(th), s = Math.sin(th), v = 1 - c;
  return [
    [c + k[0]*k[0]*v,        k[0]*k[1]*v - k[2]*s, k[0]*k[2]*v + k[1]*s],
    [k[1]*k[0]*v + k[2]*s,   c + k[1]*k[1]*v,      k[1]*k[2]*v - k[0]*s],
    [k[2]*k[0]*v - k[1]*s,   k[2]*k[1]*v + k[0]*s, c + k[2]*k[2]*v],
  ];
}

/* Nelder-Mead. Seven parameters, a smooth objective, no derivatives to get
   wrong and no dependency to add. Deliberately started from a pose that is
   already close, so this is a polish and not a search — it is not asked to
   find a camera, only to stop one leaning on four of six marks. */
function nelderMead(f, x0, step, iters) {
  const n = x0.length;
  let S = [x0.slice()];
  for (let i = 0; i < n; i++) { const p = x0.slice(); p[i] += step[i]; S.push(p); }
  let F = S.map(f);
  for (let it = 0; it < iters; it++) {
    const ord = F.map((v, i) => [v, i]).sort((a, b) => a[0] - b[0]).map(z => z[1]);
    S = ord.map(i => S[i]); F = ord.map(i => F[i]);
    const cen = Array(n).fill(0);
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) cen[j] += S[i][j] / n;
    const worst = S[n];
    const refl = cen.map((c, j) => c + (c - worst[j])), fr = f(refl);
    if (fr < F[0]) {
      const exp = cen.map((c, j) => c + 2 * (c - worst[j])), fe = f(exp);
      if (fe < fr) { S[n] = exp; F[n] = fe; } else { S[n] = refl; F[n] = fr; }
    } else if (fr < F[n - 1]) { S[n] = refl; F[n] = fr; }
    else {
      const con = cen.map((c, j) => c + 0.5 * (worst[j] - c)), fc = f(con);
      if (fc < F[n]) { S[n] = con; F[n] = fc; }
      else for (let i = 1; i <= n; i++) {
        S[i] = S[i].map((v, j) => S[0][j] + 0.5 * (v - S[0][j])); F[i] = f(S[i]);
      }
    }
  }
  const bi = F.indexOf(Math.min(...F));
  return { x: S[bi], f: F[bi] };
}

/**
 * Refine the camera over all six marks.
 *
 * @param {{w:number,h:number}} photo
 * @param {object} corners            {tl,tr,br,bl}, normalised
 * @param {{w:number,h:number}} screen  the FIRST panel, metres
 * @param {number} cornerW            the second panel's width, metres
 * @param {{tr:object,br:object}} outer  its two far marks, normalised
 * @param {object} pose               solveCalibration's pose
 * @param {boolean} [convex]          which way the second panel folds
 * @returns {{eye:number[], f:number, rmsBefore:number, rmsAfter:number}|{error:string}}
 */
export function refineCornerPose({ photo, corners, screen, cornerW, outer, pose, convex = true }) {
  if (!pose || !(photo?.w > 0) || !(photo?.h > 0)) return { error: 'no pose to refine' };
  const W = screen.w, H = screen.h, W2 = cornerW, PW = photo.w, PH = photo.h;
  if (!(W > 0 && H > 0 && W2 > 0)) return { error: 'no panel sizes to refine against' };

  const s = convex === false ? -1 : 1;
  /* Plane frame: X right, Y down, Z into the wall — the frame the pose is in.
     The second panel's outer edge sits a full W2 along Z from the hinge, which
     is the 90° corner written down; nothing here fits that angle. */
  const pts = [
    [-W/2, -H/2, 0], [W/2, -H/2, 0], [W/2, H/2, 0], [-W/2, H/2, 0],
    [W/2, -H/2, s*W2], [W/2, H/2, s*W2],
  ];
  const obs = [
    [corners.tl.x*PW, corners.tl.y*PH], [corners.tr.x*PW, corners.tr.y*PH],
    [corners.br.x*PW, corners.br.y*PH], [corners.bl.x*PW, corners.bl.y*PH],
    [outer.tr.x*PW, outer.tr.y*PH],     [outer.br.x*PW, outer.br.y*PH],
  ];
  if (obs.some(p => !Number.isFinite(p[0]) || !Number.isFinite(p[1])))
    return { error: 'a mark is not a number' };

  // n1/n2/n3 are the COLUMNS of this matrix — see core/corner.js's projectPoint
  const R0 = [
    [pose.n1[0], pose.n2[0], pose.n3[0]],
    [pose.n1[1], pose.n2[1], pose.n3[1]],
    [pose.n1[2], pose.n2[2], pose.n3[2]],
  ];

  const rms = p => {
    const R = mul(rodrigues([p[0], p[1], p[2]]), R0);
    const t = [pose.t[0] + p[3], pose.t[1] + p[4], pose.t[2] + p[5]];
    const f = pose.f + p[6];
    if (!(f > 1)) return 1e12;
    let sum = 0;
    for (let i = 0; i < pts.length; i++) {
      const P = pts[i];
      const x = R[0][0]*P[0] + R[0][1]*P[1] + R[0][2]*P[2] + t[0];
      const y = R[1][0]*P[0] + R[1][1]*P[1] + R[1][2]*P[2] + t[1];
      const z = R[2][0]*P[0] + R[2][1]*P[1] + R[2][2]*P[2] + t[2];
      if (!(z > 1e-9)) return 1e12;                     // behind the lens
      const du = pose.cx + f*x/z - obs[i][0], dv = pose.cy + f*y/z - obs[i][1];
      sum += du*du + dv*dv;
    }
    return Math.sqrt(sum / pts.length);
  };

  const zero = [0, 0, 0, 0, 0, 0, 0];
  const before = rms(zero);
  if (!Number.isFinite(before) || before > 1e11) return { error: 'the starting pose is degenerate' };

  /* Steps sized to the thing each parameter is: milliradians of rotation,
     centimetres of translation, tens of pixels of focal. */
  const best = nelderMead(rms, zero, [0.004, 0.004, 0.004, 0.05, 0.05, 0.15, 30], 3000);

  /* Refuse a refinement that did not help. Nelder-Mead on a bad start can wander
     to a flatter, sillier place — a distant telephoto reproduces almost any six
     points — so the result has to earn its way in. */
  if (!(best.f < before)) return { error: 'no improvement', rmsBefore: before, rmsAfter: before };

  const R = mul(rodrigues([best.x[0], best.x[1], best.x[2]]), R0);
  const t = [pose.t[0] + best.x[3], pose.t[1] + best.x[4], pose.t[2] + best.x[5]];
  // camera centre in the plane frame: C = -Rᵀ t
  const C = [
    -(R[0][0]*t[0] + R[1][0]*t[1] + R[2][0]*t[2]),
    -(R[0][1]*t[0] + R[1][1]*t[1] + R[2][1]*t[2]),
    -(R[0][2]*t[0] + R[1][2]*t[1] + R[2][2]*t[2]),
  ];
  const eye = [C[0], -C[1], -C[2]];                     // → Three: X right, Y up, Z toward viewer
  if (!(eye[2] > 1e-4) || !eye.every(v => Number.isFinite(v)))
    return { error: 'the refined camera fell behind the screen' };

  /* A refinement that moves the viewer a long way is not a refinement. The
     starting pose is exact on four of six marks, so the honest correction is
     small; anything large means the objective found a degenerate basin rather
     than a better camera, and the original is the safer answer. */
  const moved = Math.hypot(eye[0] - pose.C[0], eye[1] + pose.C[1], eye[2] + pose.C[2]);
  const span = Math.max(W, H, W2);
  if (moved > span * 0.5) return { error: 'the refinement wandered', rmsBefore: before, rmsAfter: best.f };

  return { eye, f: pose.f + best.x[6], rmsBefore: before, rmsAfter: best.f };
}
